// JD text-style helpers.
//
// Every JD carries a single `styles` blob keyed by role
// (global, title, subtitle, header_line, section_heading, section_content,
//  body_header_label, body_header_value, section_heading_<idx>, …).
//
// Each key holds a StyleObj — a subset of standard CSS text-styling props.
// Empty / missing fields are inherited from the fallback chain, which is:
//   field-specific  →  section-type default  →  global  →  hardcoded base.

// ---- Constants used by the editor UI. ------------------------------------
export const FONT_FAMILIES = [
    { label: "DM Sans (default)", value: "'DM Sans', sans-serif" },
    { label: "Inter", value: "'Inter', sans-serif" },
    { label: "Cormorant Garamond", value: "'Cormorant Garamond', serif" },
    { label: "System UI", value: "system-ui, sans-serif" },
    { label: "Arial", value: "Arial, Helvetica, sans-serif" },
    { label: "Georgia", value: "Georgia, serif" },
    { label: "Times New Roman", value: "'Times New Roman', Times, serif" },
    { label: "Courier New", value: "'Courier New', Courier, monospace" },
];

export const FONT_SIZES = ["12px", "14px", "16px", "18px", "20px", "24px", "32px"];

export const FONT_WEIGHTS = [
    { label: "Light 300", value: "300" },
    { label: "Regular 400", value: "400" },
    { label: "Medium 500", value: "500" },
    { label: "Semibold 600", value: "600" },
    { label: "Bold 700", value: "700" },
    { label: "Extra Bold 800", value: "800" },
];

export const TEXT_ALIGNMENTS = [
    { label: "Left", value: "left" },
    { label: "Center", value: "center" },
    { label: "Right", value: "right" },
    { label: "Justify", value: "justify" },
];

export const LINE_HEIGHTS = ["1", "1.15", "1.25", "1.4", "1.5", "1.7", "2"];

// Hardcoded base — used when neither the per-field, section, nor global style
// set a value for a property. Keeps the JD rendering deterministic.
export const BASE_JD_STYLE = {
    fontFamily: "'DM Sans', sans-serif",
    fontSize: "16px",
    fontWeight: "400",
    fontStyle: "normal",
    textDecoration: "none",
    textAlign: "left",
    lineHeight: "1.6",
};

/** Empty style shape used to initialise editor state. */
export const EMPTY_STYLE = {
    fontFamily: "",
    fontSize: "",
    fontWeight: "",
    bold: false,
    italic: false,
    underline: false,
    textAlign: "",
    lineHeight: "",
};

/**
 * Strip empty/false fields so the persisted JSON stays minimal and the
 * inheritance chain works correctly (empty === "inherit").
 */
export function pruneStyle(s) {
    if (!s || typeof s !== "object") return {};
    const out = {};
    for (const k of Object.keys(s)) {
        const v = s[k];
        if (v === "" || v === null || v === undefined) continue;
        if ((k === "bold" || k === "italic" || k === "underline") && !v) continue;
        out[k] = v;
    }
    return out;
}

/** Merge a chain of styles, later entries taking precedence. */
export function mergeStyles(...chain) {
    const out = { ...EMPTY_STYLE };
    for (const s of chain) {
        if (!s) continue;
        for (const k of Object.keys(s)) {
            const v = s[k];
            if (v === "" || v === null || v === undefined) continue;
            if ((k === "bold" || k === "italic" || k === "underline") && !v)
                continue;
            out[k] = v;
        }
    }
    return out;
}

/**
 * Resolve a style for a target field, walking the fallback chain.
 * @param {Object} styles - the full JD `styles` blob
 * @param {...string} keys - ordered chain, most specific first
 */
export function resolveStyle(styles, ...keys) {
    const chain = [];
    // Global is always the widest fallback.
    chain.push(styles?.global);
    // Then each intermediate key from LEAST → MOST specific so that the
    // final mergeStyles gives precedence to the most specific override.
    const rev = keys.slice().reverse();
    for (const k of rev) chain.push(styles?.[k]);
    return mergeStyles(...chain);
}

/**
 * Convert a resolved StyleObj to a plain CSS style object.
 * Only emits properties that were explicitly set somewhere in the chain,
 * so the caller's Tailwind classes remain the visual default and inline
 * styles only appear when the admin actively overrides a value.
 */
export function styleToCss(resolved) {
    const s = {};
    const r = resolved || {};
    if (r.fontFamily) s.fontFamily = r.fontFamily;
    if (r.fontSize) s.fontSize = r.fontSize;
    if (r.fontWeight) s.fontWeight = r.fontWeight;
    if (r.bold) {
        const explicitHeavy =
            r.fontWeight && parseInt(r.fontWeight, 10) >= 700;
        s.fontWeight = explicitHeavy ? r.fontWeight : "700";
    }
    if (r.italic) s.fontStyle = "italic";
    if (r.underline) s.textDecoration = "underline";
    if (r.textAlign) s.textAlign = r.textAlign;
    if (r.lineHeight) s.lineHeight = r.lineHeight;
    return s;
}

/** Convenience: resolve + convert in a single call. */
export function jdCss(styles, ...keys) {
    return styleToCss(resolveStyle(styles, ...keys));
}
